import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { REVIEW_REASON_CODES, ReviewReasonCode } from '@/lib/types/completion';

interface CompletionRejectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobTitle: string;
  reasonCode?: string;
  reviewNotes?: string;
  onAcknowledge: () => void;
}

export function CompletionRejectionModal({
  open,
  onOpenChange,
  jobTitle,
  reasonCode,
  reviewNotes,
  onAcknowledge,
}: CompletionRejectionModalProps) {
  const reasonLabel = reasonCode
    ? REVIEW_REASON_CODES.find((r) => r.value === reasonCode)?.label || reasonCode
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <DialogTitle className="text-lg">Completion Rejected</DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Your submission requires attention
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            Your completion submission for <span className="font-medium text-foreground">"{jobTitle}"</span> was rejected by the client.
          </p>

          {reasonLabel && (
            <div className="rounded-lg border bg-muted/50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Rejection Reason
              </p>
              <p className="text-sm font-medium">{reasonLabel}</p>
            </div>
          )}

          {reviewNotes && (
            <div className="rounded-lg border bg-muted/50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1">
                Client Comments
              </p>
              <p className="text-sm whitespace-pre-wrap">{reviewNotes}</p>
            </div>
          )}

          <p className="text-sm text-muted-foreground">
            Please address the feedback and resubmit your completion using the "Resubmit Completion" button.
          </p>
        </div>

        <DialogFooter>
          <Button onClick={onAcknowledge} className="w-full sm:w-auto">
            Acknowledge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
